/**
 * Modal nền admin — portal vào document.body, animation fade, khóa scroll body.
 *
 * Đóng bằng Escape hoặc click backdrop.
 */
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { TEXT } from '../../shared/languages';

/**
 * Modal admin dùng chung.
 * @param {Object} props
 * @param {boolean} props.isOpen
 * @param {Function} props.onClose
 * @param {string} [props.panelClassName]
 * @param {string} [props.role]
 * @param {string} [props.ariaLabelledby]
 * @param {string} [props.ariaDescribedby]
 * @param {React.ReactNode} props.children
 */
export const AdminModal = ({
  isOpen,
  onClose,
  panelClassName = '',
  role = 'dialog',
  ariaLabelledby,
  ariaDescribedby,
  children,
}) => {
  const [mounted, setMounted] = useState(isOpen);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setMounted(true);
      return undefined;
    }

    setVisible(false);
    return undefined;
  }, [isOpen]);

  useEffect(() => {
    if (!mounted || !isOpen) {
      return undefined;
    }

    const frame = window.requestAnimationFrame(() => {
      setVisible(true);
    });

    return () => window.cancelAnimationFrame(frame);
  }, [mounted, isOpen]);

  useEffect(() => {
    if (!mounted) {
      return undefined;
    }

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [mounted, onClose]);

  const handlePanelTransitionEnd = (event) => {
    if (event.currentTarget !== event.target) {
      return;
    }

    if (!visible && !isOpen) {
      setMounted(false);
    }
  };

  if (!mounted) {
    return null;
  }

  const panelClasses = ['admin-modal-panel', panelClassName].filter(Boolean).join(' ');

  return createPortal(
    <div
      className={`admin-modal ${visible ? 'is-visible' : ''}`}
      role={role}
      aria-modal="true"
      aria-labelledby={ariaLabelledby}
      aria-describedby={ariaDescribedby}
    >
      <button
        type="button"
        className="admin-modal-backdrop"
        aria-label={TEXT.common.close}
        onClick={onClose}
      />
      <div className={panelClasses} onTransitionEnd={handlePanelTransitionEnd}>
        {children}
      </div>
    </div>,
    document.body
  );
};
